// ─────────────────────────────────────────────────────────────────
// Glass-i — Fabrication Master helpers (Stage 6B.8A-3M).
//
// CRITICAL CONTRACT
//   fab_ref_name / fabRefName is a backend-only handle. It MUST
//   NOT appear in any UI surface. These helpers:
//     • normalize away any accidentally-supplied fabRefName,
//     • never include fabRefName in payloads,
//     • offer no setter for it.
// ─────────────────────────────────────────────────────────────────

export const DEFAULT_FABRICATIONS = Object.freeze([
  { fabId: 1,  fabName: 'Holes (H)',                defaultPrice: 0 },
  { fabId: 2,  fabName: 'Cutout (C)',               defaultPrice: 0 },
  { fabId: 3,  fabName: 'Shape Cut (SP)',           defaultPrice: 0 },
  { fabId: 4,  fabName: 'Big Hole (BH)',            defaultPrice: 0 },
  { fabId: 5,  fabName: 'Counter Sunk Holes (CSK)', defaultPrice: 0 },
  { fabId: 6,  fabName: 'L-Shape (L-SP)',           defaultPrice: 0 },
  { fabId: 7,  fabName: 'Boxcut (BX-CUT)',          defaultPrice: 0 },
  { fabId: 8,  fabName: 'CounterCut (C-CUT)',       defaultPrice: 0 },
  { fabId: 9,  fabName: 'WheelCut (W-C)',           defaultPrice: 0 },
  { fabId: 10, fabName: 'CornerRadius(CR)',         defaultPrice: 0 },
  { fabId: 11, fabName: 'Unassigned',               defaultPrice: 0 },
  { fabId: 12, fabName: 'Unassigned',               defaultPrice: 0 },
  { fabId: 13, fabName: 'Unassigned',               defaultPrice: 0 },
  { fabId: 14, fabName: 'Unassigned',               defaultPrice: 0 },
  { fabId: 15, fabName: 'Unassigned',               defaultPrice: 0 }
]);

let _rowIdCounter = 0;
function nextRowId() { _rowIdCounter += 1; return `fab-c-${_rowIdCounter}`; }

// Strips fab_ref_name / fabRefName even if a future backend mapper
// accidentally exposes it. Keeps the working object minimal so the
// UI cannot inadvertently render or send the handle.
export function normalizeFabricationRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => ({
    _clientRowId: r._clientRowId || (r.id ? `fab-${r.id}` : nextRowId()),
    _dirty: false,
    id:           r.id || null,
    fabId:        r.fabId != null ? Number(r.fabId) : null,
    fabName:      r.fabName || '',
    abbreviation: r.abbreviation || '',
    uom:          r.uom || 'Nos',
    defaultPrice: r.defaultPrice != null ? Number(r.defaultPrice) : 0,
    isActive:     r.isActive !== false,
    remarks:      r.remarks || ''
    // NOTE: fabRefName intentionally NOT carried into the editor.
  }));
}

export function markDirtyRow(rows, clientRowId, patch) {
  if (!Array.isArray(rows)) return [];
  // Defence in depth — drop fabRefName from any patch a caller hands
  // in. The UI never sets it; this guards against future refactors.
  const safe = { ...(patch || {}) };
  delete safe.fabRefName;
  delete safe.fab_ref_name;
  return rows.map((r) => r._clientRowId === clientRowId
    ? { ...r, ...safe, _dirty: true }
    : r);
}

export function nextFabId(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return 1;
  let max = 0;
  for (const r of rows) {
    const n = Number(r && r.fabId);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max + 1;
}

export function sortFabrications(rows) {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort((a, b) => {
    const as = a && a.fabId != null ? Number(a.fabId) : Number.POSITIVE_INFINITY;
    const bs = b && b.fabId != null ? Number(b.fabId) : Number.POSITIVE_INFINITY;
    if (as !== bs) return as - bs;
    return String((a && a.fabName) || '')
      .localeCompare(String((b && b.fabName) || ''));
  });
}

// ── Payload builders — fabRefName is ALWAYS omitted ─────────────
export function buildFabricationPayload(row) {
  if (!row || typeof row !== 'object') return {};
  const out = {
    fabName:      trimOrNull(row.fabName),
    abbreviation: trimOrNull(row.abbreviation),
    uom:          trimOrNull(row.uom) || 'Nos',
    defaultPrice: row.defaultPrice == null || row.defaultPrice === ''
                    ? 0
                    : Math.max(0, Number(row.defaultPrice)),
    isActive:     row.isActive !== false,
    remarks:      trimOrNull(row.remarks)
  };
  if (row.fabId != null && row.fabId !== '') {
    out.fabId = Math.trunc(Number(row.fabId));
  }
  // fabRefName intentionally NEVER set — backend auto-generates.
  return out;
}

export function buildFabricationBulkPayload(rows, deletedIds = []) {
  const dirty = (rows || []).filter((r) => r && r._dirty);
  return {
    rows: dirty.map((r) => {
      const out = buildFabricationPayload(r);
      if (r.id) out.id = r.id;
      if (r._clientRowId) out._clientRowId = r._clientRowId;
      // Defensive scrub — guarantees no fabRefName slips into the wire payload.
      delete out.fabRefName;
      delete out.fab_ref_name;
      return out;
    }),
    deletes: Array.isArray(deletedIds)
      ? deletedIds.filter((id) => typeof id === 'string' && id.length > 0)
      : []
  };
}

// ── Validation ──────────────────────────────────────────────────
export function validateFabricationRow(row, allRows = []) {
  const errors = [];
  if (!row || typeof row !== 'object') {
    return { ok: false, errors: [
      { field: 'row', message: 'Row is required.' }
    ] };
  }
  const name = String(row.fabName || '').trim();
  if (!name) {
    errors.push({ field: 'fabName',
      message: 'Fabrication Name is required.',
      clientRowId: row._clientRowId });
  }
  if (row.fabId != null && row.fabId !== '') {
    const n = Number(row.fabId);
    if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
      errors.push({ field: 'fabId',
        message: 'Fab ID must be greater than zero.',
        clientRowId: row._clientRowId });
    }
  }
  if (row.defaultPrice != null && row.defaultPrice !== ''
      && Number(row.defaultPrice) < 0) {
    errors.push({ field: 'defaultPrice',
      message: 'Default Price cannot be negative.',
      clientRowId: row._clientRowId });
  }
  if (Array.isArray(allRows) && allRows.length > 0) {
    const peers = allRows.filter((r) =>
      r && r._clientRowId !== row._clientRowId);
    const fabId = Number(row.fabId);
    if (Number.isFinite(fabId) && fabId > 0) {
      for (const p of peers) {
        if (Number(p.fabId) === fabId) {
          errors.push({ field: 'fabId',
            message: `Fab ID ${fabId} is already used by another row.`,
            clientRowId: row._clientRowId });
          break;
        }
      }
    }
  }
  return { ok: errors.length === 0, errors };
}

export function validateFabricationRows(rows) {
  const all = Array.isArray(rows) ? rows : [];
  const errors = [];
  for (const r of all) {
    const { ok, errors: rowErrors } = validateFabricationRow(r, all);
    if (!ok) errors.push(...rowErrors);
  }
  return { ok: errors.length === 0, errors };
}

// ── Private helpers ─────────────────────────────────────────────
function trimOrNull(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
}
